import React, { useState, useEffect, useRef } from 'react';
import './LandingPage.css';
import medallaImage from '../images/medalla-mascotags.png';

function LandingPage() {
  const [isNavVisible, setIsNavVisible] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const stepsRef = useRef(null);

  const handleWhatsAppClick = (message = '') => {
    const defaultMessage = message || 'Hola! Me interesa conocer más sobre Mascotags. ¿Podrías darme más información?';
    const encodedMessage = encodeURIComponent(defaultMessage);
    window.open(`https://wa.me/5491138161416?text=${encodedMessage}`, '_blank');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Close mobile menu first
      setIsMobileMenuOpen(false);
      
      // Force enable scroll before scrolling
      document.body.style.overflow = 'visible';
      document.documentElement.style.overflow = 'visible';
      
      // Multiple scroll methods for maximum compatibility
      const scrollMethods = [
        // Method 1: Modern scrollIntoView
        () => element.scrollIntoView({ behavior: 'smooth', block: 'start' }),
        
        // Method 2: Manual calculation
        () => {
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({ top: elementPosition, behavior: 'smooth' });
        },
        
        // Method 3: Direct scroll (fallback)
        () => {
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo(0, elementPosition);
        },
        
        // Method 4: jQuery-like scroll (last resort)
        () => {
          const elementPosition = element.offsetTop;
          window.scrollTo(0, elementPosition);
        }
      ];
      
      // Try each method until one works
      let methodIndex = 0;
      const tryScroll = () => {
        if (methodIndex < scrollMethods.length) {
          try {
            scrollMethods[methodIndex]();
          } catch (error) {
            methodIndex++;
            setTimeout(tryScroll, 50);
          }
        }
      };
      
      tryScroll();
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      setIsNavVisible(scrollTop > 100);
    };

    // AGGRESSIVE MOBILE SCROLL FIX
    const forceMobileScroll = () => {
      // Remove any blocking styles
      document.documentElement.style.overflow = 'visible';
      document.documentElement.style.height = 'auto';
      document.body.style.overflow = 'visible';
      document.body.style.height = 'auto';
      document.body.style.position = 'static';
      
      // Force scroll on all containers
      const containers = document.querySelectorAll('.landing-page, section, .hero');
      containers.forEach(container => {
        container.style.overflow = 'visible';
        container.style.height = 'auto';
        container.style.position = 'static';
      });
      
      // Enable touch scrolling
      document.body.style.webkitOverflowScrolling = 'touch';
      document.documentElement.style.webkitOverflowScrolling = 'touch';
    };

    // Apply fixes immediately
    forceMobileScroll();
    
    // Apply fixes on any interaction
    const events = ['touchstart', 'touchmove', 'touchend', 'scroll', 'click', 'load'];
    events.forEach(event => {
      document.addEventListener(event, forceMobileScroll, { passive: true });
    });

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      events.forEach(event => {
        document.removeEventListener(event, forceMobileScroll);
      });
    };
  }, []);

  // Auto-carousel for mobile in "Cómo funciona"
  useEffect(() => {
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    const container = stepsRef.current;
    if (!isMobile || !container) return;

    let index = 0;
    const getCards = () => Array.from(container.children || []);
    const cards = getCards();
    if (cards.length === 0) return;

    const scrollToIndex = (i) => {
      const card = cards[i % cards.length];
      if (!card) return;
      const left = card.offsetLeft;
      container.scrollTo({ left, behavior: 'smooth' });
    };

    const intervalId = setInterval(() => {
      index = (index + 1) % cards.length;
      scrollToIndex(index);
    }, 3000);

    // Pause on user interaction
    const pause = () => clearInterval(intervalId);
    container.addEventListener('touchstart', pause, { passive: true });
    container.addEventListener('mousedown', pause);

    return () => {
      clearInterval(intervalId);
      container.removeEventListener('touchstart', pause);
      container.removeEventListener('mousedown', pause);
    };
  }, []);

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className={`navbar ${isNavVisible ? 'navbar-visible' : ''}`}>
        <div className="nav-container">
          <div className="nav-logo">
            <img src="/logomascotags.png" alt="Logo Mascotags" className="logo-huella" />
            <span className="logo-text">MASCO<span className="logo-highlight">TAGS</span></span>
          </div>
          
          {/* Mobile menu button */}
          <button 
            className="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <span className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>

          {/* Desktop navigation */}
          <div className="nav-links">
            <button className="nav-link" onClick={() => scrollToSection('hero')}>Inicio</button>
            <button className="nav-link" onClick={() => scrollToSection('what-is')}>Nosotros</button>
            <button className="nav-link" onClick={() => scrollToSection('how-it-works')}>¿Cómo funciona?</button>
          </div>

          {/* Mobile navigation */}
          <div className={`mobile-nav ${isMobileMenuOpen ? 'active' : ''}`}>
            <button className="mobile-nav-link" onClick={() => scrollToSection('hero')}>Inicio</button>
            <button className="mobile-nav-link" onClick={() => scrollToSection('what-is')}>Nosotros</button>
            <button className="mobile-nav-link" onClick={() => scrollToSection('how-it-works')}>¿Cómo funciona?</button>
          </div>
        </div>
      </nav>

      {/* Botón WhatsApp Flotante */}
      <div className="floating-whatsapp" onClick={() => handleWhatsAppClick()}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
        </svg>
      </div>

      {/* Hero Section */}
      <section id="hero" className="hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              #MASCO<span className="highlight">TAGS</span>
            </h1>
            <h2 className="hero-subtitle">
              La huella digital de tu mejor amig@
            </h2>
            <p className="hero-description">
              Identificación inteligente para mascotas<br/>
              QR único + Conexión inmediata<br/>
              Tecnología que protege
            </p>
            <button 
              className="cta-button primary"
              onClick={() => handleWhatsAppClick('Hola! Quiero hacer un pedido de Mascotags. ¿Podrías ayudarme?')}
            >
              Hacer pedido por WhatsApp
            </button>
          </div>
        </div>
      </section>

      {/* ¿Qué es Mascotags? */}
      <section id="what-is" className="what-is">
        <div className="container">
          <div className="section-content">
            <div className="text-content">
              <h2>¿Por qué nace Mascotags?</h2>
              <p>
                <strong>Nacimos con una misión muy simple:</strong> que tu mascota nunca pierda el camino a casa.
              </p>
              
              <p>
                En Mascotags diseñamos <strong>medallas identificatorias con QR</strong> que almacenan la información esencial de tu mejor amig@ y lo conectan con vos en segundos si alguna vez se pierde.
              </p>
              
              <p>
                Cuando alguien encuentra a tu peludo, simplemente <strong>escanea el código QR</strong> y puede contactarte inmediatamente por WhatsApp con su ubicación exacta.
              </p>
              
              <p>
                Creemos que ellos son familia y merecen el mejor cuidado posible. <strong>Tecnología y amor trabajando juntos</strong> para mantenerlos siempre seguros.
              </p>
            </div>
            <div className="image-content">
              <img 
                src={medallaImage} 
                alt="Medalla Mascotags de cerca" 
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ¿Cómo funciona? */}
      <section id="how-it-works" className="how-it-works">
        <div className="container">
          <h2>¿Cómo funciona?</h2>
          <p className="how-it-works-subtitle">Seguí estos pasos para registrar el QR de tu mascota ¡Simple y sin registro previos!</p>
          <div className="steps" ref={stepsRef}>
            <div className="step">
              <div className="step-icon">1</div>
              <h3>ESCANEAS EL QR</h3>
              <p>Escaneas el código QR de tu medalla Mascotags.</p>
            </div>
            <div className="step">
              <div className="step-icon">2</div>
              <h3>REGISTRAS LOS DATOS</h3>
              <p>Completas la información de tu mascota en el formulario.</p>
            </div>
            <div className="step">
              <div className="step-icon">3</div>
              <h3>¡LISTO!</h3>
              <p>Quien escanee el QR verá tus datos y podrá contactarte.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section id="final-cta" className="final-cta">
        <div className="container">
          <div className="cta-content">
            <h2>La protección que tu mascota merece.</h2>
            <p>
              Con #Mascotags, tu mejor amig@ siempre va a tener una forma de volver a vos.
            </p>
            <button 
              className="cta-button primary large"
              onClick={() => handleWhatsAppClick('Hola! Quiero pedir Mascotags para mi mascota. ¿Podrías ayudarme con el proceso?')}
            >
              Pedir mi Mascotag ahora
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>
            &copy; {new Date().getFullYear()} Diseñado y desarrollado por{' '}
            <a href="http://higherweb.com.ar" target="_blank" rel="noopener noreferrer" style={{color: 'var(--mascotags-yellow)', textDecoration: 'none'}}>
              HIGHERWEB
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
